// Service for the tenant registration console.
// Uses its own HTTP client so the registrar token never mixes with the app session.
import axios, { type AxiosInstance } from 'axios';
import { config, js } from '../config/env';
import type { TenantData } from '../types';
import type { RegisterTenantRequest, TenantListItem } from '../types/tenantRegistration';

const REGISTRAR_TOKEN_KEY = 'C2_tenantRegistrar_token';

export function normalizeBaseUrl(url: string): string
{
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

/** Default API base URL (the same one the app uses for authentication) */
export function defaultServerUrl(): string
{
  return normalizeBaseUrl(String(js.settings.apiAuthURL || config.api.authUrl || ''));
}

/** Builds the public URL of a tenant from the .env template */
export function buildTenantUrl(tenancyName: string): string
{
  const template = config.tenantRegistration.tenantUrlTemplate || '';
  return normalizeBaseUrl(template.replace('{tenant}', tenancyName.trim()));
}

/** URL of the first-time site configuration (initial wizard) for a tenant */
export function buildFirstSetupUrl(tenantUrl: string): string
{
  const path = (config.tenantRegistration.firstSetupPath || '').replace(/^\//, '');
  return normalizeBaseUrl(tenantUrl) + path;
}

export function extractApiError(error: unknown, fallback: string): string
{
  const err = error as { response?: { data?: any }; message?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data?.error?.message) return data.error.message;
  if (data?.Message) return data.Message;
  if (data?.message) return data.message;
  if (data?.ModelState)
  {
    const messages = Object.values(data.ModelState as Record<string, string[]>).flat();
    if (messages.length) return messages.join(' ');
  }
  if (err?.message) return err.message;
  return fallback;
}

class TenantRegistrationService
{
  private client(serverUrl?: string, token?: string | null): AxiosInstance
  {
    const base = normalizeBaseUrl(serverUrl || defaultServerUrl());
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token)
    {
      headers.Authorization = `Bearer ${token}`;
    }
    return axios.create({ baseURL: `${base}api/`, headers, timeout: 120000 });
  }

  // ==================== Registrar session ====================

  getRegistrarToken(): string | null
  {
    return sessionStorage.getItem(REGISTRAR_TOKEN_KEY);
  }

  clearRegistrarToken(): void
  {
    sessionStorage.removeItem(REGISTRAR_TOKEN_KEY);
  }

  /** Validates the fixed registrar account configured in .env */
  validateRegistrarCredentials(user: string, password: string): boolean
  {
    const expectedUser = config.tenantRegistration.user;
    const expectedPassword = config.tenantRegistration.password;
    if (!expectedUser || !expectedPassword) return false;
    return (
      user.trim().toLowerCase() === expectedUser.trim().toLowerCase() &&
      password === expectedPassword
    );
  }

  /**
   * Obtains an API token for the registrar. GetTenants requires the host-side
   * "Pages.Tenants" permission, which only the host administrator has, so the
   * login goes through Account/AdminAuthenticate against the host (empty tenancy)
   * or the tenancy configured in .env. Returns null when the API rejects the
   * login; registration itself is anonymous and still works without it.
   */
  async authenticateRegistrar(user: string, password: string): Promise<string | null>
  {
    try
    {
      const response = await this.client().post<{
        result?: { data?: string; user?: string; fullname?: string };
        success?: boolean;
        data?: string;
      }>('Account/AdminAuthenticate', {
        usernameOrEmailAddress: user,
        password,
        // Empty tenancy = host database (Corgee admin)
        tenancyName: config.tenantRegistration.tenancyName || '',
      });
      const token = response.data?.result?.data || response.data?.data || null;
      if (token)
      {
        sessionStorage.setItem(REGISTRAR_TOKEN_KEY, token);
      }
      return token;
    }
    catch (error)
    {
      console.warn('⚠️ Registrar host login failed, tenant list will use local data only.', error);
      return null;
    }
  }

  // ==================== Tenants ====================

  /** POST api/Tenant/RegisterTenant. Returns the tenant URL reported by the API. */
  async registerTenant(request: RegisterTenantRequest): Promise<string>
  {
    const response = await this.client(undefined, this.getRegistrarToken()).post<string>(
      'Tenant/RegisterTenant',
      request,
    );
    const url = typeof response.data === 'string' ? response.data : '';
    return url ? normalizeBaseUrl(url) : buildTenantUrl(request.TenancyName);
  }

  /** GET api/Tenant/GetTenants (requires registrar token) */
  async getTenants(getDeleted: boolean = false): Promise<TenantListItem[]>
  {
    const token = this.getRegistrarToken();
    if (!token)
    {
      throw new Error('No registrar API session');
    }
    const response = await this.client(undefined, token).get<TenantListItem[]>(
      'Tenant/GetTenants',
      { params: { getDeleted } },
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /** GET api/Tenant/GetTenant against a given server, used to test the configured server */
  async getTenantFromServer(serverUrl: string, tenancyName: string): Promise<TenantData | null>
  {
    const response = await this.client(serverUrl).get<TenantData | null>('Tenant/GetTenant', {
      params: { tenancyName },
    });
    return response.data || null;
  }

  /**
   * Re-sends an access email to the tenant administrator.
   * The API has no endpoint to resend the welcome email, so this triggers the
   * password reset email for the admin account of that tenant.
   */
  async resendAccessEmail(serverUrl: string, tenancyName: string, email: string): Promise<void>
  {
    const params = new URLSearchParams({ email, tenantString: tenancyName });
    await this.client(serverUrl).post(`account/forgetPassword?${params.toString()}`);
  }
}

export const tenantRegistrationService = new TenantRegistrationService();
export default tenantRegistrationService;
